import { useState, useEffect, ReactNode } from "react";
import { pwaManager, isPWAInstalled } from "@/utils/pwaUtils";
import { PWAOfflineScreen } from "./PWAOfflineScreen";

interface PWAOfflineWrapperProps {
  children: ReactNode;
}

export function PWAOfflineWrapper({ children }: PWAOfflineWrapperProps) {
  const [isOnline, setIsOnline] = useState(pwaManager.isOnline());
  const [showOfflineScreen, setShowOfflineScreen] = useState(false);

  useEffect(() => {
    // Only show offline screen for PWA users
    const isPWA = isPWAInstalled() || pwaManager.isStandalone();

    const cleanup = pwaManager.onNetworkChange((online) => {
      setIsOnline(online);

      if (isPWA) {
        if (!online) {
          // Show offline screen after a brief delay to avoid flashing
          const timer = setTimeout(() => {
            setShowOfflineScreen(true);
          }, 500);
          return () => clearTimeout(timer);
        } else {
          // Hide offline screen immediately when back online
          setShowOfflineScreen(false);

          // Update last sync time
          localStorage.setItem("lastSyncTime", new Date().toISOString());
        }
      }
    });

    // Initial check for PWA offline state
    if (isPWA && !isOnline) {
      setShowOfflineScreen(true);
    }

    return cleanup;
  }, []);

  const handleRetry = () => {
    // Force a network check
    if (navigator.onLine) {
      setIsOnline(true);
      setShowOfflineScreen(false);
      window.location.reload();
    }
  };

  // Show offline screen only for PWA users when offline
  if (showOfflineScreen && !isOnline) {
    return <PWAOfflineScreen onRetry={handleRetry} />;
  }

  return <>{children}</>;
}
